import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { NotificationsProvider } from './notifications';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { buildTheme } from './muiTheme';
import { DARK_MODE_EVENT, getDarkMode, initDarkMode } from './darkMode';
import { installViewportUnits } from './viewportUnits';

// Legacy cleanup: remove the old UI variant query param from the URL.
// This keeps old bookmarks working without leaving `UI=b` in the address bar.
(function stripLegacyUiQueryParam() {
  try {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    let changed = false;

    for (const key of Array.from(params.keys())) {
      if (String(key).toLowerCase() === 'ui') {
        params.delete(key);
        changed = true;
      }
    }

    if (!changed) return;

    const nextSearch = params.toString();
    const nextUrl = url.pathname + (nextSearch ? `?${nextSearch}` : '') + (url.hash || '');
    window.history.replaceState(window.history.state, '', nextUrl);
  } catch {
    // no-op
  }
})();

function Root() {
  const [dark, setDark] = useState(() => initDarkMode());

  useEffect(() => {
    return installViewportUnits();
  }, []);

  useEffect(() => {
    function onDarkModeChanged() {
      setDark(getDarkMode());
    }

    function onStorage(e) {
      if (e && e.key === 'monsurv_dark_mode') onDarkModeChanged();
    }

    window.addEventListener(DARK_MODE_EVENT, onDarkModeChanged);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(DARK_MODE_EVENT, onDarkModeChanged);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const theme = useMemo(() => buildTheme({ mode: dark ? 'dark' : 'light' }), [dark]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationsProvider>
        <App />
      </NotificationsProvider>
    </ThemeProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);

